import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest } from 'redux-saga/effects'
import {
  makeGetRequest,
  makePostRequest,
  makePutRequest,
  makeDeleteRequest,
  makeLeadsGetRequest
} from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { utcDate } from 'utils/helpers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import {
  toCalendarTasks,
  fromEvent,
  toCalendarEvents,
  toCalendarDeals,
  toCalendarLeads
} from './serializers'
import isArray from 'lodash/isArray'
import moment from 'moment'

const getDateFilters = dateRange => {
  let start
  let end
  if (isArray(dateRange)) {
    if (dateRange.length === 1) {
      start = moment(dateRange[0]).startOf('day').format()
      end = moment(dateRange[0]).endOf('day').format()
    }
    if (dateRange.length > 1) {
      start = dateRange[0]
      end = moment(dateRange[6]).endOf('day').format()
    }
  } else {
    if (typeof dateRange === 'object') {
      start = dateRange.start
      end = dateRange.end
    }
  }
  return {
    fromDate: utcDate(start),
    toDate: utcDate(end)
  }
}

function* fetchCalendarTasks() {
  try {
    const url = '/v1/tasks/user'

    const tasks = yield makeGetRequest(url)
    yield put({
      type: ACT.CALENDAR_TASKS_FETCH_SUCCESS,
      payload: {
        tasks: toCalendarTasks(tasks)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCalendarBids(action) {
  const { id } = action.payload
  try {
    const url = `/v1/deals-calendar/bid?userIds=${id}`

    const deals = yield makeGetRequest(url)
    yield put({
      type: ACT.CALENDAR_BIDS_FETCH_SUCCESS,
      payload: {
        deals: toCalendarDeals(deals)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Bids',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCalendarLeads(action) {
  const { id, users } = action.payload
  try {
    const url = `/v1/leads/calendar/followup?userIds=${id}`

    const leads = yield makeLeadsGetRequest(url)
    yield put({
      type: ACT.CALENDAR_LEADS_FETCH_SUCCESS,
      payload: {
        leads: toCalendarLeads(leads, users)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Leads',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCalendarFollowups(action) {
  const { id } = action.payload
  try {
    const url = `/v1/deals-calendar/followup?userIds=${id}`

    const deals = yield makeGetRequest(url)
    yield put({
      type: ACT.CALENDAR_FOLLOWUPS_FETCH_SUCCESS,
      payload: {
        deals: toCalendarDeals(deals, true)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar FollowUps',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCalendarEvents() {
  try {
    const url = '/v1/events/user'

    const events = yield makeGetRequest(url)
    yield put({
      type: ACT.CALENDAR_EVENTS_FETCH_SUCCESS,
      payload: {
        events: toCalendarEvents(events)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Events',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchUserCalendarTasks(action) {
  const { ids } = action.payload
  try {
    let url = `/v1/tasks/users/${ids.join()}`
    if (!ids.length) {
      url = '/v1/tasks/all'
    }
    const tasks = yield makeGetRequest(url)
    yield put({
      type: ACT.CALENDAR_TASKS_FETCH_SUCCESS,
      payload: {
        tasks: toCalendarTasks(tasks)
      }
    })

    yield put({
      type: ACT.CALENDAR_TASKS_BY_USER_FETCH_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchUserCalendarBids(action) {
  const { ids, dateRange, isAdmin } = action.payload

  let url = `/v1/deals-calendar/bid?userIds=${!isAdmin ? '' : ids.length ? ids.join() : -1}`

  if (dateRange) {
    const { fromDate, toDate } = getDateFilters(dateRange)
    url = `/v1/deals-calendar/bid?userIds=${
      !isAdmin ? '' : ids.length ? ids.join() : -1
    }&fromDate=${fromDate}&toDate=${toDate}`
  }

  try {
    const deals = yield makeGetRequest(url)

    yield put({
      type: ACT.CALENDAR_BIDS_FETCH_SUCCESS,
      payload: {
        deals: toCalendarDeals(deals)
      }
    })

    yield put({
      type: ACT.CALENDAR_BIDS_BY_USER_FETCH_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Bids',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchUserCalendarLeads(action) {
  const { ids, users } = action.payload
  try {
    let url = `/v1/leads/calendar/followup?userIds=${ids.length ? ids.join() : -1}`
    const leads = yield makeLeadsGetRequest(url)

    yield put({
      type: ACT.CALENDAR_LEADS_FETCH_SUCCESS,
      payload: {
        leads: toCalendarLeads(leads, users)
      }
    })

    yield put({
      type: ACT.CALENDAR_LEADS_BY_USER_FETCH_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Leads',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchUserCalendarFollowups(action) {
  const { ids, dateRange, isAdmin } = action.payload

  let url = `/v1/deals-calendar/followup?userIds=${!isAdmin ? '' : ids.length ? ids.join() : -1}`

  if (dateRange) {
    const { fromDate, toDate } = getDateFilters(dateRange)
    url = `/v1/deals-calendar/followup?userIds=${
      !isAdmin ? '' : ids.length ? ids.join() : -1
    }&fromDate=${fromDate}&toDate=${toDate}`
  }

  try {
    const deals = yield makeGetRequest(url)

    yield put({
      type: ACT.CALENDAR_FOLLOWUPS_FETCH_SUCCESS,
      payload: {
        deals: toCalendarDeals(deals, true)
      }
    })

    yield put({
      type: ACT.CALENDAR_FOLLOWUPS_BY_USER_FETCH_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar FollowUps',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchUserCalendarEvents(action) {
  const { ids } = action.payload
  try {
    let url = `/v1/events/users/${ids.join()}`
    if (!ids.length) {
      url = '/v1/events/all'
    }
    const events = yield makeGetRequest(url)

    yield put({
      type: ACT.CALENDAR_EVENTS_FETCH_SUCCESS,
      payload: {
        events: toCalendarEvents(events)
      }
    })

    yield put({
      type: ACT.CALENDAR_EVENTS_BY_USER_FETCH_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Calendar Events',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* saveEvent(action) {
  const { event } = action.payload
  try {
    const url = '/v1/events'

    const response = yield makePostRequest(url, fromEvent(event))
    yield put({
      type: ACT.CALENDAR_EVENT_SAVE_SUCCESS,
      payload: {
        event: toCalendarEvents([response])[0]
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Calendar Event',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateEvent(action) {
  const { event } = action.payload
  try {
    const url = `/v1/events/${event.id}`

    const response = yield makePutRequest(url, fromEvent(event))
    yield put({
      type: ACT.CALENDAR_EVENT_UPDATE_SUCCESS,
      payload: {
        event: toCalendarEvents([response])[0]
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Calendar Event',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteEvent(action) {
  const { id } = action.payload
  try {
    const url = `/v1/events/${id}`

    yield makeDeleteRequest(url)
    yield put({
      type: ACT.CALENDAR_EVENT_DELETE_SUCCESS,
      payload: {
        id
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Calendar Event',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const calendarSagas = [
  takeLatest(ACT.CALENDAR_TASKS_FETCH_REQUESTED, fetchCalendarTasks),
  takeLatest(ACT.CALENDAR_BIDS_FETCH_REQUESTED, fetchCalendarBids),
  takeLatest(ACT.CALENDAR_LEADS_FETCH_REQUESTED, fetchCalendarLeads),
  takeLatest(ACT.CALENDAR_FOLLOWUPS_FETCH_REQUESTED, fetchCalendarFollowups),
  takeLatest(ACT.CALENDAR_EVENTS_FETCH_REQUESTED, fetchCalendarEvents),
  takeLatest(ACT.CALENDAR_TASKS_BY_USER_FETCH_REQUESTED, fetchUserCalendarTasks),
  takeLatest(ACT.CALENDAR_BIDS_BY_USER_FETCH_REQUESTED, fetchUserCalendarBids),
  takeLatest(ACT.CALENDAR_LEADS_BY_USER_FETCH_REQUESTED, fetchUserCalendarLeads),
  takeLatest(ACT.CALENDAR_FOLLOWUPS_BY_USER_FETCH_REQUESTED, fetchUserCalendarFollowups),
  takeLatest(ACT.CALENDAR_EVENTS_BY_USER_FETCH_REQUESTED, fetchUserCalendarEvents),
  takeLatest(ACT.CALENDAR_EVENT_SAVE_REQUESTED, saveEvent),
  takeLatest(ACT.CALENDAR_EVENT_UPDATE_REQUESTED, updateEvent),
  takeLatest(ACT.CALENDAR_EVENT_DELETE_REQUESTED, deleteEvent)
]

export default calendarSagas
